import { useTranslation } from "react-i18next";
import type { JSX } from "react";
import { memo, useMemo } from "react";

import classes from "./styles.module.scss";

import type { IFieldProps } from ".";

import { Icon } from "@components/atoms/icons";
import { Typography } from "@components/atoms/typography";

export const Field = memo(function Field<T>(props: IFieldProps<T>) {
  const { t } = useTranslation("form");
  const { errors, touched } = props.formik;
  const error = errors[props.name];
  const touch = touched[props.name];

  const icon = useMemo(
    () =>
      props.icon && (
        <Icon
          {...(typeof props.icon === "string"
            ? { name: props.icon }
            : props.icon)}
        />
      ),
    [props.icon],
  );
  const title = useMemo(
    () =>
      (props.title || props.customTitle) && (
        <Typography fontSize={"md-high"} {...props.titleProps}>
          {props.customTitle || (props.title && t(props.title)) || ""}
          {props.isRequired && (
            <Typography createSpan variant={"inherit"} color={"danger-main"}>
              {"*"}
            </Typography>
          )}
        </Typography>
      ),
    [props.customTitle, props.isRequired, props.title, props.titleProps, t],
  );

  return (
    <div className={classes.container}>
      {!props?.noLabel && (
        <label htmlFor={props.name}>
          {(icon || title) && (
            <div className={classes.label}>
              {icon}
              {title}
            </div>
          )}
        </label>
      )}
      {typeof props.children === "function"
        ? props.children(
            touched[props.name] && errors[props.name] ? "error" : undefined,
          )
        : props.children}
      <div className={classes.errorContainer}>
        {touch && error ? (
          <label htmlFor={props.name}>
            <Typography
              color={"danger-dark"}
              fontSize={"sm"}
              className={classes.error}
            >
              {error.toString()}
            </Typography>
          </label>
        ) : null}
      </div>
    </div>
  );
}) as <T>(props: IFieldProps<T>) => JSX.Element;
